import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuFileSpreadsheet, LuTrendingUp, LuUserCheck, LuUserX, LuUsers, LuWallet } from 'react-icons/lu';
import DataCardList from '../../../components/data/DataCardList';
import DataTable from '../../../components/data/DataTable';
import DataToolbar from '../../../components/data/DataToolbar';
import FilterSheet from '../../../components/data/FilterSheet';
import PageHero from '../../../components/data/PageHero';
import StatTiles from '../../../components/data/StatTiles';
import TablePagination from '../../../components/data/TablePagination';
import Button from '../../../components/ui/Button';
import useElementWidth from '../../../hooks/useElementWidth';
import useMediaQuery from '../../../hooks/useMediaQuery';
import usePagedList from '../../../hooks/usePagedList';
import useTableColumns from '../../../hooks/useTableColumns';
import useViewportFit from '../../../hooks/useViewportFit';
import BudgetPricingModal from './BudgetPricingModal';
import PaymentsModal from './PaymentsModal';
import SalaryModal from './SalaryModal';
import ZamModal from './ZamModal';
import useEmployeeFinance from './useEmployeeFinance';
import useEmployeeFinanceText from './employeeFinanceText';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  EDUCATION_OPTIONS,
  EF_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  STATUS_VALUES,
  WORK_TYPES,
  getFilterState,
} from './employeeFinanceColumns';
import EmployeeFinanceCell, { EmployeeFinanceCardRow, useFieldText } from './parts/EmployeeFinanceCell';
import EmployeeFinanceDetailPane from './parts/EmployeeFinanceDetailPane';

const COLUMNS_STORAGE_KEY = 'employeeFinance.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';
const WORK_TYPE_KEYS = { 'Full Time': 'fullTime', 'Part Time': 'partTime' };

/**
 * Çalışan Finans Yönetimi (Tailwind), built like the other data pages:
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (Aktif / İnaktif / Tümü switch, header filters, column menu), the
 * selected employee's panel with Maaş, Ödemeler and Bütçe Ücretlendirme,
 * Toplu Zam and the Excel export in the header.
 */
const EmployeeFinanceManagementPage = () => {
  const {
    employees, isLoading, filteredEmployees, stats, filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    selectedEmployee, setSelectedId, downloadExcel,
  } = useEmployeeFinance();
  const { language, locale, label } = useEmployeeFinanceText();
  const fieldText = useFieldText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: EF_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [dialog, setDialog] = useState(null); // salary | payments | pricing | raise
  const hasLoaded = employees.length > 0 || !isLoading;

  const resetKey = JSON.stringify([filters, searchTerm]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredEmployees, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedEmployee) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEmployee, showFilterSheet, setSelectedId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);

  const selectOptions = useMemo(() => ({
    Active: [{ value: '1', label: label('status.active') }, { value: '0', label: label('status.inactive') }],
    isHandicappedPerson: [{ value: 'Yes', label: label('yesNo.yes') }, { value: 'No', label: label('yesNo.no') }],
    isIntern: [{ value: 'Yes', label: label('yesNo.yes') }, { value: 'No', label: label('yesNo.no') }],
    isRetired: [{ value: 'Yes', label: label('yesNo.yes') }, { value: 'No', label: label('yesNo.no') }],
    educationInfo: EDUCATION_OPTIONS.map((value) => ({ value, label: value })),
    workType: WORK_TYPES.map((value) => ({ value, label: label(`workTypes.${WORK_TYPE_KEYS[value]}`) })),
  }), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: EF_COLUMNS[key].type, options: selectOptions[key] },
  })), [columns.visibleKeys, columnLabel, selectOptions]);

  const chipValue = (key) => {
    const option = selectOptions[key]?.find((item) => item.value === String(filters[key]));
    return option ? option.label : String(filters[key]);
  };
  // Aktif / İnaktif seçimi araç çubuğundaki düğmede görünür; çip olarak tekrarlanmaz
  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'Active' && EF_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: chipValue(key), state: 'applied' }));

  const statusValue = filters.Active ? String(filters.Active) : '';
  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm);

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedEmployee ? filteredEmployees.findIndex((emp) => emp.id === selectedEmployee.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredEmployees[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const shareOf = (count) => (stats.total > 0 ? Math.min(1, count / stats.total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const setStatus = (value) => handleFilter('Active', value);
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: stats.total, icon: LuUsers, gradient: 'tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: stats.active, icon: LuUserCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.active), shareTitle: shareTitle(stats.active), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => setStatus(statusValue === '1' ? '' : '1'), active: statusValue === '1' },
    { key: 'inactive', label: label('stats.inactive'), shortLabel: label('stats.inactiveShort'), value: stats.inactive, icon: LuUserX, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', onClick: () => setStatus(statusValue === '0' ? '' : '0'), active: statusValue === '0' },
  ];

  const paneProps = {
    employee: selectedEmployee,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredEmployees.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredEmployees.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
    onSalary: () => setDialog('salary'),
    onPayments: () => setDialog('payments'),
    onBudgetPricing: () => setDialog('pricing'),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuWallet}
          gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
          actionsClassName="tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap lg:tw-justify-end"
        >
          <Button onClick={downloadExcel} aria-label={label('actions.excel')} title={label('actions.excelHint')} className="max-sm:tw-w-10 max-sm:tw-px-0">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            <span className="tw-hidden sm:tw-inline">{label('actions.excel')}</span>
          </Button>
          <Button variant="primary" onClick={() => setDialog('raise')} title={label('actions.raiseHint')} className="max-sm:tw-w-full">
            <LuTrendingUp aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.raise')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: STATUS_VALUES.map((value) => ({ value, label: label(`toolbar.status_${value || 'all'}`) })),
                value: statusValue,
                onChange: setStatus,
                layoutId: 'employee-finance-status',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(employee) => <EmployeeFinanceCardRow employee={employee} />}
                selectedId={selectedEmployee?.id}
                onSelect={(employee) => setSelectedId(employee.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(employee, key) => <EmployeeFinanceCell columnKey={key} employee={employee} />}
                getCellTitle={(employee, key) => fieldText(employee, key) || undefined}
                selectedId={selectedEmployee?.id}
                onSelect={(employee) => setSelectedId(employee.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredEmployees.length}
              rangeOf={label('table.rangeOf', { total: filteredEmployees.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <EmployeeFinanceDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <EmployeeFinanceDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key), type: EF_COLUMNS[key].type, options: selectOptions[key] }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredEmployees.length}
      />

      {dialog === 'salary' && selectedEmployee && <SalaryModal selectedEmployee={selectedEmployee} onClose={() => setDialog(null)} />}
      {dialog === 'payments' && selectedEmployee && <PaymentsModal selectedEmployee={selectedEmployee} onClose={() => setDialog(null)} />}
      <BudgetPricingModal selectedEmployee={selectedEmployee} show={dialog === 'pricing'} onClose={() => setDialog(null)} />
      {dialog === 'raise' && <ZamModal onClose={() => setDialog(null)} />}
    </>
  );
};

export default EmployeeFinanceManagementPage;
